import { useState, useEffect, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import NiceModal from "@ebay/nice-modal-react";
import ModalManager from "@ebay/nice-modal-react";
import ApiModal from "/src/components/modal/ApiModal/ApiModal";
import IconButton from "/src/components/UI/Button/IconButton/IconButton";
import Select from "react-select";
import ResultBadge from "../ResultBadge/ResultBadge";
import CriteriaDefinitionModal from "../CriteriaDefinitionModal/CriteriaDefinitionModal";
import criteriaAdminService from "../../services/criteriaAdminService";
import { useToastDispatchContext } from "/src/layouts/toast/context/toastContext";
import "./CriteriaDefinitionTable.css";

const CRITERIA_TYPES = [
    { value: "Inbound", label: "Inbound" },
    { value: "Non sale", label: "Non sale" },
    { value: "Retention", label: "Retention" },
    { value: "Technical support", label: "Technical support" },
];

function CriteriaDefinitionTable() {
    const toast = useToastDispatchContext();
    const { getDefinitions, deleteDefinition, getCategories } = criteriaAdminService;
    const [selectedType, setSelectedType] = useState(CRITERIA_TYPES[0]);
    const [categories, setCategories] = useState([]);
    const [rows, setRows] = useState([]);

    const fetchCategories = async () => {
        const [response, error] = await getCategories();
        if (error || !response) return categories;
        setCategories(response);
        return response;
    };

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        const fetchDefinitions = async () => {
            const [response, error] = await getDefinitions({ criteria_type: selectedType.value });
            if (error || !response) {
                toast.error("Error", "Unable to load criteria");
                return;
            }
            setRows(response);
        };
        fetchDefinitions();
    }, [selectedType]);

    const categoryNameById = useMemo(
        () => categories.reduce((acc, c) => ({ ...acc, [c.id]: c.name }), {}),
        [categories]
    );

    const onClickAdd = async () => {
        const freshCategories = await fetchCategories();
        NiceModal.show(CriteriaDefinitionModal, {
            title: "Create a new criteria",
            criteriaType: selectedType.value,
            categories: freshCategories,
        }).then((definition) => setRows((prev) => [definition, ...prev]));
    };

    const onClickUpdate = async (row) => {
        const freshCategories = await fetchCategories();
        NiceModal.show(CriteriaDefinitionModal, {
            title: "Edit criteria",
            data: row,
            categories: freshCategories,
        }).then((definition) => setRows((prev) => prev.map((r) => (r.id === definition.id ? definition : r))));
    };

    const onClickDelete = (row) =>
        ModalManager.show(ApiModal, {
            title: "Delete criteria " + row.name,
            body: "Do you really want to delete criteria " + row.name + "? Past conversations that detected it will keep showing it as an unknown criteria.",
            api: { request: deleteDefinition, param: [row.id] },
            buttons: [
                { className: "validate", label: "Cancel", style: { marginRight: "20px" } },
                { className: "cancel", label: "Delete", type: "submit" },
            ],
        }).then((res) => {
            if (res?.error != null) {
                toast.error("Error", res.error);
                return;
            }
            setRows((prev) => prev.filter((r) => r.id !== row.id));
        });

    const columnDefs = useMemo(() => [
        { headerName: "Name", field: "name", flex: 1, minWidth: 180, sortable: true },
        {
            headerName: "Category",
            field: "category_id",
            width: 220,
            valueFormatter: (params) => categoryNameById[params.value] || "—",
        },
        { headerName: "Weight", field: "weight", width: 100, sortable: true },
        {
            headerName: "Result",
            field: "result",
            width: 120,
            cellStyle: { justifyContent: "center" },
            cellRenderer: (params) => <ResultBadge value={params.value} />,
        },
        {
            headerName: "Color",
            field: "color",
            width: 90,
            cellStyle: { justifyContent: "center" },
            cellRenderer: (params) => (
                <div style={{ width: "20px", height: "20px", borderRadius: "4px", background: params.value }}></div>
            ),
        },
        {
            headerName: "Actions",
            width: 100,
            pinned: "right",
            cellRenderer: (params) => (
                <div style={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                    <i className="fas fa-edit action-button blue" onClick={() => onClickUpdate(params.data)} />
                    <i className="fas fa-trash action-button red" onClick={() => onClickDelete(params.data)} />
                </div>
            ),
        },
    ], [categoryNameById, categories]);

    return (
        <div className="criteria-admin-grid-section">
            <div className="criteria-definition-toolbar">
                <Select
                    value={selectedType}
                    options={CRITERIA_TYPES}
                    onChange={setSelectedType}
                    isSearchable={false}
                    styles={{ container: (base) => ({ ...base, width: "260px" }) }}
                />
                <IconButton
                    className="main-iconButton profil_action criteria-admin-add-button"
                    icon="fas fa-plus"
                    onClick={onClickAdd}
                    title="Add criteria"
                />
            </div>
            <div className="ag-theme-alpine criteria-admin-grid" style={{ width: "100%", height: "650px" }}>
                <AgGridReact
                    enableCellTextSelection={true}
                    rowData={rows}
                    rowHeight={56}
                    columnDefs={columnDefs}
                    defaultColDef={{ sortable: true, filter: true, resizable: true }}
                    pagination={true}
                    paginationPageSize={50}
                />
            </div>
        </div>
    );
}

export default CriteriaDefinitionTable;
